import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/system_settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { extractErrorMessage, toastError } from "@/lib/api-errors";
import { useDebounced } from "@/lib/use-debounced";

const TARGET_LABELS: Record<api.InspectionRuleTarget, string> = {
  lab: "Лаборатория",
  okk: "ОКК",
  skip: "Не проверять",
};
const TARGETS = Object.keys(TARGET_LABELS) as api.InspectionRuleTarget[];

const RULES_KEY = ["inspection-routing-rules"] as const;

const selectClass = "h-9 rounded-md border bg-background px-2 text-sm";

/** Живой поиск группы номенклатуры в 1С (только папки, с путём). */
function GroupSearch({
  onPick,
}: {
  onPick: (g: api.NomenclatureGroup) => void;
}): React.ReactElement {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const dq = useDebounced(q.trim(), 300);

  const found = useQuery({
    queryKey: ["nomenclature-groups", dq],
    queryFn: () => api.searchNomenclatureGroups(dq),
    enabled: dq.length >= 2,
    staleTime: 60_000,
  });

  return (
    <div className="relative">
      <Input
        aria-label="Поиск группы номенклатуры"
        placeholder="Группа номенклатуры в 1С (от 2 символов)"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && dq.length >= 2 && (
        <div className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded border bg-popover p-1 shadow-md">
          {found.isLoading && <p className="p-2 text-sm text-muted-foreground">Поиск…</p>}
          {found.isError && (
            <p className="p-2 text-sm text-destructive">
              {extractErrorMessage(found.error, "Ошибка поиска в 1С")}
            </p>
          )}
          {(found.data ?? []).map((g) => (
            <button
              key={g.ref}
              type="button"
              className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
              onClick={() => {
                onPick(g);
                setQ("");
                setOpen(false);
              }}
            >
              {g.path || g.name}
            </button>
          ))}
          {found.isSuccess && found.data.length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">Ничего не найдено</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Маршрут входного контроля по ГРУППАМ номенклатуры 1С: куда после приёмки
 * едет позиция — в лабораторию, в ОКК или никуда.
 *
 * Правила — в базе портала, не в коде: группы у каждой инсталляции свои, а
 * решение «что проверяет ОКК» меняется без выкатки.
 */
export function InspectionRoutingCard(): React.ReactElement {
  const qc = useQueryClient();
  const rules = useQuery({ queryKey: RULES_KEY, queryFn: api.listInspectionRules });
  const [picked, setPicked] = useState<api.NomenclatureGroup | null>(null);
  const [target, setTarget] = useState<api.InspectionRuleTarget | "">("");

  const save = useMutation({
    mutationFn: (v: { ref: string; target: api.InspectionRuleTarget }) =>
      api.saveInspectionRule(v.ref, v.target),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RULES_KEY });
      toast.success("Правило сохранено");
    },
    onError: toastError("Не удалось сохранить правило"),
  });
  const drop = useMutation({
    mutationFn: api.deleteInspectionRule,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RULES_KEY });
      toast.success("Правило удалено");
    },
    onError: toastError("Не удалось удалить правило"),
  });

  const add = () => {
    if (!picked || !target) return;
    save.mutate(
      { ref: picked.ref, target },
      {
        onSuccess: () => {
          setPicked(null);
          setTarget("");
        },
      },
    );
  };

  const list = rules.data?.rules ?? [];
  const warning = rules.data?.warning ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Маршрутизация входного контроля</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Правило группы действует на все её подгруппы; ближайшая к позиции группа
          выигрывает. Позиции без правила уходят в ОКК с пометкой «группа не
          настроена».
        </p>

        <div className="grid gap-2 sm:grid-cols-[1fr_minmax(0,10rem)_auto] sm:items-start">
          <div className="space-y-1">
            <GroupSearch onPick={setPicked} />
            {picked && (
              <p className="text-xs text-muted-foreground">
                Выбрана: <span className="font-medium">{picked.path || picked.name}</span>
              </p>
            )}
          </div>
          <select
            aria-label="Куда направлять новую группу"
            className={selectClass}
            value={target}
            onChange={(e) => setTarget(e.target.value as api.InspectionRuleTarget | "")}
          >
            <option value="">Куда…</option>
            {TARGETS.map((t) => (
              <option key={t} value={t}>
                {TARGET_LABELS[t]}
              </option>
            ))}
          </select>
          <Button disabled={!picked || !target || save.isPending} onClick={add}>
            Добавить
          </Button>
        </div>

        {rules.isLoading && <p className="text-sm text-muted-foreground">Загрузка правил…</p>}
        {rules.isError && (
          <p className="text-sm text-destructive">
            {extractErrorMessage(rules.error, "Не удалось загрузить правила")}
          </p>
        )}
        {warning && (
          <div
            role="alert"
            className="rounded-md border border-yellow-400 bg-yellow-50 px-3 py-2 text-sm text-yellow-900"
          >
            {warning}
          </div>
        )}
        {list.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1 font-medium">Группа</th>
                <th className="py-1 font-medium">Куда</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {list.map((r) => {
                const label = r.group_path || r.group_name || r.group_ref;
                return (
                  <tr key={r.group_ref} className="border-t">
                    <td className="py-1.5 pr-2">{label}</td>
                    <td className="py-1.5 pr-2">
                      <select
                        aria-label={`Куда: ${label}`}
                        className={selectClass}
                        value={r.target}
                        disabled={save.isPending}
                        onChange={(e) =>
                          save.mutate({
                            ref: r.group_ref,
                            target: e.target.value as api.InspectionRuleTarget,
                          })
                        }
                      >
                        {TARGETS.map((t) => (
                          <option key={t} value={t}>
                            {TARGET_LABELS[t]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Удалить правило: ${label}`}
                        disabled={drop.isPending}
                        onClick={() => drop.mutate(r.group_ref)}
                      >
                        ✕
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
